import type { FC } from 'react';
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { GridPagination, SuspenseBoundary } from 'components/common';
import { useAdsHistoryHypothesis } from 'features/cabinet/facebook/api';
import AdsHistoryGroupedFilters from 'features/cabinet/facebook/components/AdsHistoryGroupedFilters';
import type { AdsHistoryGroupMode } from 'features/cabinet/facebook/components/AdsHistoryGroupedView';
import AdsHistoryGroupedView from 'features/cabinet/facebook/components/AdsHistoryGroupedView';
import type { AdsHistoryGroupedEntry } from 'features/cabinet/facebook/components/AdsHistoryGroupedView.mockData';
import type { AdsHistoryHypothesisResponse } from 'features/cabinet/facebook/api/getAdsHistoryHypothesis';
import { applyGroupedFilters, EMPTY_FILTERS, type AdsHistoryGroupedFilterValues } from 'features/cabinet/facebook/utils/adsHistoryGroupedFilters';
import { usePagination } from 'hooks/usePagination';

const normalizeEntries = (data: AdsHistoryHypothesisResponse): AdsHistoryGroupedEntry[] =>
  data.data.flatMap((week) =>
    week.groups.flatMap((group) =>
      group.packs.flatMap((pack) =>
        pack.ads.map((ad) => ({
          ...ad,
          pack_name: pack.pack_name,
          hypothesis: group.hypothesis ?? '',
          email: pack.representative.created_by ?? '',
          created_at: pack.representative.created_at ?? '',
          ad_account: pack.representative.ad_account,
          ad_set: pack.representative.adset,
          message: ad.message ?? pack.representative.message ?? '',
          status: ad.status ?? pack.representative.status,
          ad_id: ad.ad_id ?? '',
        })),
      ),
    ),
  );

const TestingHistoryByHypothesisFilters: FC<{
  filters: AdsHistoryGroupedFilterValues;
  onFilterChange: (next: AdsHistoryGroupedFilterValues) => void;
  pagination: ReturnType<typeof usePagination>;
}> = ({ filters, onFilterChange, pagination }) => {
  const { data } = useAdsHistoryHypothesis({
    params: {
      marketing_process: 'testing',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const entries = useMemo(() => normalizeEntries(data), [data]);

  return (
    <AdsHistoryGroupedFilters
      entries={entries}
      filters={filters}
      onFilterChange={onFilterChange}
    />
  );
};

const TestingHistoryByHypothesisGrid: FC<{
  mode: AdsHistoryGroupMode;
  filters: AdsHistoryGroupedFilterValues;
  pagination: ReturnType<typeof usePagination>;
}> = ({ mode, filters, pagination }) => {
  const { data } = useAdsHistoryHypothesis({
    params: {
      marketing_process: 'testing',
      limit: pagination.perPage,
      offset: pagination.page,
    },
  });

  const entries = useMemo(() => normalizeEntries(data), [data]);
  const filteredEntries = useMemo(() => applyGroupedFilters(entries, filters), [entries, filters]);

  return (
    <>
      <AdsHistoryGroupedView
        entries={filteredEntries}
        mode={mode}
        emptyPlaceholder="No testing ads by hypothesis posted recently."
      />
      <GridPagination
        count={data.count}
        isLoading={false}
        page={pagination.page}
        perPage={pagination.perPage}
        handlePageChange={pagination.handlePageChange}
        handleRowsPerPageChange={pagination.handleRowsPerPageChange}
      />
    </>
  );
};

const TestingHistoryByHypothesisPage: FC = () => {
  const pagination = usePagination([], 50);
  const [filters, setFilters] = useState<AdsHistoryGroupedFilterValues>(EMPTY_FILTERS);

  return (
    <Box sx={{ p: 0 }}>
      <SuspenseBoundary>
        <TestingHistoryByHypothesisFilters
          filters={filters}
          onFilterChange={setFilters}
          pagination={pagination}
        />
      </SuspenseBoundary>
      <SuspenseBoundary>
        <TestingHistoryByHypothesisGrid
          mode="hypothesis"
          filters={filters}
          pagination={pagination}
        />
      </SuspenseBoundary>
    </Box>
  );
};

export default TestingHistoryByHypothesisPage;
